/**
 * Application Constants - Centralized configuration values
 * Provides a single source of truth for all magic numbers and configuration values
 */

// Rendering Constants
export const RENDERING = {
	// Default radius for dot renderer
	DOT_RADIUS: 1,

	// SVG namespace
	SVG_NAMESPACE: "http://www.w3.org/2000/svg",

	// Gradient configuration
	SHARED_GRADIENT_ID: "shared-triangulation-gradient",
	GRADIENT_START_OFFSET: "0%",
	GRADIENT_END_OFFSET: "100%",

	// Default gradient coordinates
	GRADIENT_X1: "0%",
	GRADIENT_Y1: "0%",
	GRADIENT_X2: "100%",
	GRADIENT_Y2: "100%",
} as const;

// Validation Constants
export const VALIDATION = {
	// Points validation bounds
	MIN_POINTS: 3,
	MAX_POINTS: 10000,
	COORDINATE_MIN: -10000,
	COORDINATE_MAX: 10000,

	// Edge length validation
	MIN_EDGE_LENGTH: 1,
	MAX_EDGE_LENGTH: 10000,

	// File validation
	MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
	ALLOWED_IMAGE_TYPES: [
		"image/jpeg",
		"image/jpg",
		"image/png",
		"image/webp",
	] as readonly string[],
	ALLOWED_IMAGE_EXTENSIONS: [
		"jpg",
		"jpeg",
		"png",
		"webp",
	] as readonly string[],
} as const;

// Worker Configuration
export const WORKER = {
	// Default number of workers (fallback for unsupported browsers)
	DEFAULT_WORKER_COUNT: 4,

	// Task ID generation
	TASK_ID_PREFIX: "task_",
	TASK_ID_RANDOM_LENGTH: 9,
} as const;

// UI Configuration
export const UI = {
	// Menu configuration
	SAVE_FEEDBACK_DURATION: 2000, // milliseconds

	// Default canvas dimensions
	DEFAULT_CANVAS_WIDTH: 800,
	DEFAULT_CANVAS_HEIGHT: 600,

	// Default color values
	DEFAULT_GRADIENT_START: "#ff0000",
	DEFAULT_GRADIENT_END: "#0000ff",

	// Range input defaults
	DEFAULT_MESH_DENSITY: {
		MIN: 10,
		MAX: 500,
		DEFAULT: 100,
	},
} as const;

// Performance Configuration
export const PERFORMANCE = {
	// Batch size for DOM operations
	DOM_BATCH_SIZE: 100,

	// Memory management
	MAX_TRIANGLES_IN_MEMORY: 50000,

	// Debounce delays (milliseconds)
	CANVAS_UPDATE_DEBOUNCE: 16, // ~60fps
	WORKER_CLEANUP_DELAY: 100,
} as const;

// Storage Keys
export const STORAGE_KEYS = {
	CANVAS_STATE: "delaunay-canvas-state",
	EDITOR_STATE: "delaunay-editor-state",
	USER_PREFERENCES: "delaunay-user-preferences",
} as const;

// Error Messages
export const ERROR_MESSAGES = {
	// Worker errors
	WORKER_CREATION_FAILED: "Failed to create worker",
	WORKER_TASK_TIMEOUT: "Worker task timed out",
	WORKER_UNEXPECTED_ERROR: "Unexpected worker error occurred",

	// Validation errors
	INVALID_POINTS_ARRAY: "Invalid points array provided",
	POINTS_OUT_OF_BOUNDS: "Points are outside allowed boundaries",
	INSUFFICIENT_POINTS: "Insufficient number of points for triangulation",

	// File errors
	FILE_TOO_LARGE: "File size exceeds maximum limit",
	UNSUPPORTED_FILE_TYPE: "Unsupported file type",
	FILE_PROCESSING_FAILED: "Failed to process file",

	// SVG errors
	SVG_EXPORT_FAILED: "Failed to export SVG",
	SVG_ELEMENT_NOT_FOUND: "SVG element not found",
} as const;

// Feature Flags
export const FEATURE_FLAGS = {
	ENABLE_WORKER_DEBUGGING: false,
	ENABLE_PERFORMANCE_MONITORING: false,
	ENABLE_ERROR_REPORTING: true,
	ENABLE_ADVANCED_VALIDATION: true,
} as const;

// Export grouped constants for convenience
export const CONFIG = {
	RENDERING,
	VALIDATION,
	WORKER,
	UI,
	PERFORMANCE,
	STORAGE_KEYS,
	ERROR_MESSAGES,
	FEATURE_FLAGS,
} as const;

export default CONFIG;
